<script lang="ts">
	import __ from '$lib/stores/TranslationStore';
	import { createEventDispatcher, onMount } from 'svelte';
	import StepNavigator from '../Stepper/StepNavigator.svelte';
	import { getStepper } from '../Stepper/stepper';
	import SimulationHeader, { type SVGComponent } from './SimulationHeader.svelte';
	import { fade } from 'svelte/transition';
	import ResultFrame from './Result/ResultFrame.svelte';
	import Cta from '$lib/components/Global/Cta.svelte';
	import { page } from '$app/stores';

	export let title: string = 'Simulador';
	export let caption: string = 'Crédito';
	export let icon: SVGComponent | undefined = undefined;
	export let showInitialContent: boolean = false;
	export let popup = false;

	let initialContentShown = showInitialContent;
	let loading = true;
	let panelLoading = false;
	let hasError = false;

	const stepper = getStepper();
	const result = false;

	let header: HTMLElement | undefined;
	const getHeader = (node: HTMLDivElement) => {
		if (node.firstChild?.nodeName.toLowerCase() === 'header') {
			header = node.firstChild as HTMLElement;
		}
	};

	export async function doLoading(callback: () => void) {
		try {
			panelLoading = true;
			await callback();
			panelLoading = false;
		} catch (error) {
			console.error(error);
			panelLoading = false;
			hasError = true;
		}
	}

	const dispatch = createEventDispatcher();

	onMount(() => {
		loading = false;
	});

	function hideInitialContent() {
		initialContentShown = false;
	}
</script>

<div
	class={!$page.data.embed
		? 'h-[calc(100dvh-60px)] lg:h-[calc(100dvh-162px)]'
		: 'h-screen lg:h-[calc(100dvh-82px)]'}
>
	<div class="header hidden bg-slate lg:block">
		<SimulationHeader topHeader header={title} {icon} {caption} />
	</div>
	<div class="relative flex h-full w-full bg-light text-dark lg:pt-0 {!result ? '' : 'h-full'}">
		{#if popup}
			<slot name="popup" />
		{/if}
		{#if loading}
			<div
				transition:fade
				class="absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-light"
			>
				<div
					class="h-12 w-12 animate-spin rounded-full
                    border-4 border-solid border-primary border-t-transparent"
				/>
			</div>
		{/if}
		<StepNavigator />
		<div
			class="relative flex w-full flex-col overflow-y-auto bg-white {!result ? '' : ''}"
			use:getHeader
		>
			<div
				class:basis-48={!$stepper.showResult}
				class="header form-shadow-el"
				class:lg:hidden={$stepper.showResult}
			>
				<SimulationHeader inline={$stepper.showResult} header={title} {icon} {caption} />
			</div>
			<div
				class="wrapper flex flex-1 items-center justify-center px-5 pb-8 {!$stepper.showResult
					? 'md:px-10'
					: ''}"
			>
				{#if hasError}
					<slot name="error">
						<div
							transition:fade
							class="absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-light"
						>
							<section class="mb-20 flex flex-col items-start gap-5 px-5 md:items-center">
								<div class="flex flex-col items-center gap-3">
									<h2 class="text-lg text-dark lg:text-2xl">
										{$__('Ocorreu um imprevisto ao fazer sua simulação.')}
									</h2>
									<h3 class="text-sm lg:text-base">
										{$__('Mas não se preocupe. Os nosso agentes entrarão em contacto.')}
									</h3>
								</div>
								<div class="w-ful flex flex-col items-center md:mx-auto md:flex-row md:gap-10">
									<button
										on:click={() => {
											dispatch('retry');
											hasError = false;
										}}
										class="order-last my-5 w-full text-center font-semibold text-primary md:w-auto md:px-24 lg:order-first"
									>
										<u class="whitespace-nowrap border-b-2 border-primary no-underline">
											{$__('Tentar novamente')}
										</u>
									</button>
								</div>
							</section>
						</div>
					</slot>
				{/if}
				{#if panelLoading}
					<div
						transition:fade
						class="absolute bottom-0 left-0 right-0 top-0 z-50 flex items-center justify-center bg-light"
					>
						<div
							class="h-12 w-12 animate-spin rounded-full
                    border-4 border-solid border-primary border-t-transparent"
						/>
					</div>
				{/if}
				<main class="z-10 box-border grid h-full w-full overflow-x-hidden lg:w-auto lg:self-start">
					<slot {stepper} />
					<slot name="content" />

					{#if initialContentShown}
						<section class="absolute left-0 top-0 h-full w-full" transition:fade>
							<slot name="initial-content" {hideInitialContent} />
						</section>
					{/if}
				</main>
				{#if !$stepper.showResult}
					<div class="hidden shrink grow-0 lg:block xl:basis-48" />
				{/if}
			</div>
		</div>
	</div>
</div>
